import React, { useState } from 'react';
import { Trophy, Medal } from 'lucide-react';
import { usePlayerRankingStats } from '@/hooks/usePlayerRankingStats';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface Props {
  userId?: string;
}

const PlayerTournamentStats: React.FC<Props> = ({ userId }) => {
  const { stats, loading } = usePlayerRankingStats(userId);
  const { t } = useLanguage();
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (loading) {
    return (
      <div className="mb-4">
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  if (!stats.length) return null;

  const row = stats[selectedIndex] || stats[0];

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 px-1">
        <Trophy className="h-4 w-4" />
        {t('profile.tournamentStats')}
      </h3>

      {/* Subcategory selector */}
      <div className="flex gap-2 overflow-x-auto pb-2 px-1 scrollbar-hide">
        {stats.map((s, idx) => (
          <button
            key={s.id}
            onClick={() => setSelectedIndex(idx)}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-left transition-colors ${
              selectedIndex === idx
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <p className="text-xs font-medium leading-tight">{s.category} {s.subcategory}</p>
            <p className="text-[10px] opacity-75 leading-tight">{s.sport}</p>
          </button>
        ))}
      </div>

      <div className="bg-card rounded-xl shadow-sm border overflow-hidden mt-1">
        {/* Header */}
        <div className="grid grid-cols-[1.2fr_0.6fr_1fr_0.6fr_0.6fr_0.8fr] gap-0 px-3 py-2.5 bg-muted/50 text-xs font-medium text-muted-foreground border-b">
          <span className="text-center">{t('profile.format')}</span>
          <span className="text-center">T</span>
          <span className="text-center">Oct/QF</span>
          <span className="text-center">SF</span>
          <span className="text-center">
            <Medal className="h-3.5 w-3.5 mx-auto text-slate-400" />
          </span>
          <span className="text-center">
            <Trophy className="h-3.5 w-3.5 mx-auto text-yellow-500" />
          </span>
        </div>

        {/* Circuito row */}
        <div className="grid grid-cols-[1.2fr_0.6fr_1fr_0.6fr_0.6fr_0.8fr] gap-0 px-3 py-2.5 items-center">
          <div className="flex justify-center">
            <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5 bg-muted">
              {t('profile.circuit')}
            </Badge>
          </div>
          <span className="text-sm text-center text-muted-foreground">{row.tournaments_circuito || 0}</span>
          <span className="text-sm text-center text-foreground">{row.circuito_qf || '-'}</span>
          <span className="text-sm text-center text-foreground">{row.circuito_sf || '-'}</span>
          <span className="text-sm text-center text-foreground">{row.circuito_subcampeon || '-'}</span>
          <span className="text-sm text-center font-semibold text-foreground">{row.circuito_champion || '-'}</span>
        </div>

        {/* Relámpago row */}
        <div className="grid grid-cols-[1.2fr_0.6fr_1fr_0.6fr_0.6fr_0.8fr] gap-0 px-3 py-2.5 items-center bg-muted/20 border-t border-dashed">
          <div className="flex justify-center">
            <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5 bg-emerald-100 text-emerald-700 border-emerald-200">
              {t('profile.blitz')}
            </Badge>
          </div>
          <span className="text-sm text-center text-muted-foreground">{row.tournaments_relampago || 0}</span>
          <span className="text-sm text-center text-foreground">{row.relampago_qf || '-'}</span>
          <span className="text-sm text-center text-foreground">{row.relampago_sf || '-'}</span>
          <span className="text-sm text-center text-foreground">{row.relampago_subcampeon || '-'}</span>
          <span className="text-sm text-center font-semibold text-foreground">{row.relampago_champion || '-'}</span>
        </div>
      </div>
    </div>
  );
};

export default PlayerTournamentStats;
